<template>
    <div class="prd-tab">
        <div class="prd-hd">
            <div>
                <h2>Produtos</h2>
                <div class="prd-sub">Todos os produtos desta cotação, digitados ou não</div>
            </div>
            <div class="prd-summary">
                <div class="summary-card">
                    <span>Produtos</span>
                    <b>{{ pagination.total || 0 }}</b>
                </div>
            </div>
        </div>

        <div class="prd-filterbar">
            <a-input-group compact class="prd-search-group">
                <a-select
                    v-model="searchField"
                    class="prd-search-group-field"
                    allow-clear
                    placeholder="Buscar por"
                    :dropdown-style="{ minWidth: '160px' }"
                >
                    <a-select-option v-for="option in searchFieldOptions" :key="option.key" :value="option.key">
                        {{ option.label }}
                    </a-select-option>
                </a-select>
                <a-input
                    v-model="search"
                    placeholder="Buscar"
                    allow-clear
                    class="prd-search-group-input"
                    :disabled="!searchField"
                    @pressEnter="filtrar"
                />
            </a-input-group>

            <a-select
                class="prd-filter-select fixed-height-select"
                placeholder="Reabastecimento"
                v-model="replenishmentFilter"
                mode="multiple"
                allow-clear
            >
                <a-select-option v-for="option in replenishmentOptions" :key="option.key" :value="option.key">
                    {{ option.label }}
                </a-select-option>
            </a-select>

            <a-select
                class="prd-filter-select fixed-height-select"
                placeholder="Status"
                v-model="statusFilter"
                mode="multiple"
                allow-clear
            >
                <a-select-option v-for="option in statusOptions" :key="option.key" :value="option.key">
                    {{ option.label }}
                </a-select-option>
            </a-select>

            <a-button type="primary" @click="filtrar">Filtrar</a-button>

            <a-dropdown :trigger="['click']">
                <a-button :loading="exporting">
                    Exportar
                    <a-icon type="down" />
                </a-button>

                <a-menu slot="overlay">
                    <a-menu-item @click="exportProducts('excel')">
                        Exportar Excel
                    </a-menu-item>

                    <a-menu-item @click="exportProducts('pdf')">
                        Exportar PDF
                    </a-menu-item>
                </a-menu>
            </a-dropdown>
        </div>

        <a-card :bordered="false" class="prd-card" :style="selectedRows.length && { marginBottom: '60px' }">
            <a-table
                size="small"
                :columns="columns"
                :data-source="rows"
                :loading="loading"
                row-key="pro_id"
                :pagination="pagination"
                :row-selection="rowSelection"
                :locale="{ emptyText: 'Nenhum produto encontrado nesta cotação.' }"
                @change="handleTableChange"
            >
                <template slot="produto" slot-scope="text, product">
                    <div class="pro-name">{{ product.pro_descricao }}</div>
                    <div class="pro-ean" v-if="product.pro_ean || product.pro_codInterno">
                        <template v-if="product.pro_ean">EAN {{ product.pro_ean }}</template>
                        <template v-if="product.pro_ean && product.pro_codInterno"> · </template>
                        <template v-if="product.pro_codInterno">Cód. {{ product.pro_codInterno }}</template>
                    </div>
                </template>

                <template slot="ressuprimento" slot-scope="text, product">
                    <span class="repl-tag" v-if="replenishment(product)">{{ replenishment(product) }}</span>
                    <span v-else>--</span>
                </template>

                <template slot="embalagem" slot-scope="text, product">{{ product.pro_embalagem || "—" }}</template>

                <template slot="estoque" slot-scope="text, product">{{ product.estoque != null ? $options.filters.stock(product.estoque) : "—" }}</template>

                <template slot="pedido" slot-scope="text, product">{{ product.soma_quantidades || product.quantidade || "—" }}</template>

                <template slot="status" slot-scope="text, product">
                    <span v-if="product.purchase_status === 'NO_OFFER'" class="req-empty">Sem oferta</span>
                    <span v-else class="status-tag" :class="{ 'status-tag-purchased': product.purchase_status === 'ORDERED' }">
                        {{ product.purchase_status === 'ORDERED' ? "Comprado" : "Não comprado" }}
                    </span>
                </template>

                <template slot="ofertas" slot-scope="text, product">{{ product.offers_count || 0 }}</template>

                <template slot="menorPreco" slot-scope="text, product">
                    <span v-if="product.min_offer_price != null">{{ product.min_offer_price | currency }}</span>
                    <span v-else>—</span>
                </template>

                <template slot="ultimoPreco" slot-scope="text, product">
                    <span v-if="product.last_price != null">{{ product.last_price | currency }}</span>
                    <span v-else>—</span>
                </template>

                <template slot="observacao" slot-scope="text, product">{{ product.observacao || "—" }}</template>

                <template slot="acoes" slot-scope="text, product">
                    <ActionMenu :actions="productActions(product)" />
                </template>
            </a-table>
        </a-card>

        <SelectionFooter
            :count="selectedRows.length"
            item-label="produtos selecionados"
        >
            <template slot="actions">
                <a-button size="large" @click="clearSelection">Limpar seleção</a-button>
                <a-button size="large" type="primary" :loading="creatingRequest" @click="createRequisicao">Criar requisição</a-button>
            </template>
        </SelectionFooter>
    </div>
</template>

<script>
import RequestProductService from "@/services/v3/requests/request-products.service";
import RequestService from "@/services/v3/request.service";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import { REPLENISHMENT_LABELS, replenishmentLabel } from "@/helpers/replenishment";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";
import SelectionFooter from "@/components/cliente/monitors/SelectionFooter.vue";

export default {
    name: "ProductsTab",
    components: { ActionMenu, SelectionFooter },
    data() {
        return {
            svc: new RequestProductService(),
            requestSvc: new RequestService(),
            loading: false,
            rows: [],
            search: "",
            searchField: undefined,
            searchFieldOptions: [
                { key: "name", label: "Produto" },
                { key: "ean", label: "EAN" },
                { key: "internal_code", label: "Código" },
            ],
            replenishmentFilter: [],
            statusFilter: [],
            savingReplenishment: null,
            exporting: false,
            selectedRowKeys: [],
            selectedMap: {},
            creatingRequest: false,
            sort: { field: "produto", order: "ascend" },
            pagination: {
                current: 1,
                pageSize: 50,
                total: 0,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
            },
        };
    },
    computed: {
        columns() {
            return [
                {
                    title: "Produto",
                    key: "produto",
                    columnKey: "produto",
                    scopedSlots: { customRender: "produto" },
                    sorter: true,
                    sortOrder: this.sort.field === "produto" ? this.sort.order : false,
                },
                { title: "Reabastecimento", key: "ressuprimento", align: "center", scopedSlots: { customRender: "ressuprimento" } },
                { title: "Status", key: "status", align: "center", scopedSlots: { customRender: "status" } },
                { title: "Qtd. Emb.", key: "embalagem", align: "center", scopedSlots: { customRender: "embalagem" } },
                { title: "Estoque", key: "estoque", align: "center", scopedSlots: { customRender: "estoque" } },
                { title: "Pedido", key: "pedido", align: "center", scopedSlots: { customRender: "pedido" } },
                { title: "Qtd. Ofertas", key: "ofertas", align: "center", scopedSlots: { customRender: "ofertas" } },
                { title: "Menor Preço", key: "menorPreco", align: "right", scopedSlots: { customRender: "menorPreco" } },
                { title: "Último Preço", key: "ultimoPreco", align: "right", scopedSlots: { customRender: "ultimoPreco" } },
                { title: "Observação", key: "observacao", scopedSlots: { customRender: "observacao" } },
                { title: "Ações", key: "acoes", width: 60, scopedSlots: { customRender: "acoes" } },
            ];
        },
        replenishmentOptions() {
            return Object.keys(REPLENISHMENT_LABELS).map((key) => ({ key, label: REPLENISHMENT_LABELS[key] }));
        },
        statusOptions() {
            return [
                { key: "NO_OFFER", label: "Sem oferta" },
                { key: "NOT_ORDERED", label: "Não comprado" },
                { key: "ORDERED", label: "Comprado" },
            ];
        },
        selectedRows() {
            return Object.values(this.selectedMap);
        },
        rowSelection() {
            return {
                selectedRowKeys: this.selectedRowKeys,
                onChange: (selectedRowKeys, selectedRows) => {
                    this.selectedRowKeys = selectedRowKeys;
                    selectedRows.forEach((row) => this.$set(this.selectedMap, row.pro_id, row));

                    const keptKeys = selectedRowKeys.map(String);
                    Object.keys(this.selectedMap).forEach((key) => {
                        if (!keptKeys.includes(key)) this.$delete(this.selectedMap, key);
                    });
                },
                onSelectAll: async (selected) => {
                    if (this.pagination.total <= this.pagination.pageSize) return;

                    if (selected) {
                        const result = await this.$swal({
                            title: "Selecionar produtos",
                            text: "Deseja selecionar apenas a página atual ou todos os produtos filtrados?",
                            icon: "question",
                            showCancelButton: true,
                            confirmButtonText: "Todos",
                            cancelButtonText: "Página atual",
                        });
                        if (result.isConfirmed) this.selectAllFiltered();
                        return;
                    }

                    const result = await this.$swal({
                        title: "Remover seleção",
                        text: "Deseja remover apenas a página atual ou todos os produtos filtrados?",
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonText: "Todos",
                        cancelButtonText: "Página atual",
                    });
                    if (result.isConfirmed) this.deselectAllFiltered();
                },
            };
        },
    },
    watch: {
        searchField() {
            this.search = "";
        },
    },
    created() {
        this.load();
    },
    methods: {
        replenishment(product) {
            return replenishmentLabel(product.replenishment_strategy);
        },
        filtrar() {
            this.load(1);
        },
        exportProducts(type) {
            this.exporting = true;

            this.svc
                .products(
                    this.$route.params.id,
                    { ...this.buildParams(this.pagination.current), export: type },
                    { responseType: "blob" }
                )
                .then((response) => {
                    const blob = new Blob([response.data]);
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");

                    link.href = url;
                    link.download = `produtos-cotacao-${this.$route.params.id}.${type === "pdf" ? "pdf" : "xlsx"}`;
                    link.click();

                    window.URL.revokeObjectURL(url);
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.exporting = false;
                });
        },
        clearSelection() {
            this.selectedRowKeys = [];
            this.selectedMap = {};
        },
        async selectAllFiltered() {
            this.loading = true;

            try {
                const { data } = await this.svc.products(this.$route.params.id, { ...this.buildParams(this.pagination.current), export: "ids" });
                const ids = Array.isArray(data.data) ? data.data : [];

                ids.forEach((id) => this.$set(this.selectedMap, id, { pro_id: id }));
                this.selectedRowKeys = Object.values(this.selectedMap).map((row) => row.pro_id);

                this.$swal.fire({
                    icon: "success",
                    title: "Seleção concluída",
                    text: `${ids.length} produtos adicionados à seleção`,
                });
            } catch (error) {
                ErrorHandlerService.handle(error, this.$store);
            } finally {
                this.loading = false;
            }
        },
        async deselectAllFiltered() {
            this.loading = true;

            try {
                const { data } = await this.svc.products(this.$route.params.id, { ...this.buildParams(this.pagination.current), export: "ids" });
                const ids = Array.isArray(data.data) ? data.data : [];

                ids.forEach((id) => this.$delete(this.selectedMap, id));
                this.selectedRowKeys = Object.values(this.selectedMap).map((row) => row.pro_id);
            } catch (error) {
                ErrorHandlerService.handle(error, this.$store);
            } finally {
                this.loading = false;
            }
        },
        createRequisicao() {
            if (!this.selectedRows.length) return;

            this.$swal
                .fire({
                    icon: "question",
                    title: "Criar requisição",
                    text: `Criar uma nova requisição por loja com ${this.selectedRows.length} produto(s) selecionado(s), baseada nas solicitações originais desta cotação? Os produtos continuam nesta cotação.`,
                    showCancelButton: true,
                    confirmButtonText: "Criar requisição",
                    cancelButtonText: "Cancelar",
                })
                .then((result) => {
                    if (result.isConfirmed) this.submitRequisicao();
                });
        },
        submitRequisicao() {
            const productIds = this.selectedRows.map((product) => product.pro_id);

            this.creatingRequest = true;

            this.requestSvc
                .replicate(this.$route.params.id, null, productIds)
                .then((response) => {
                    const total = (response.data.created_request_ids || []).length;

                    this.$swal.fire(
                        "Feito!",
                        total
                            ? `${total} nova(s) requisição(ões) criada(s) no monitor de ressuprimento.`
                            : "Nenhuma loja com os produtos selecionados para replicar.",
                        total ? "success" : "warning"
                    );
                    this.clearSelection();
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.creatingRequest = false;
                });
        },
        buildParams(page) {
            const params = {
                page,
                per_page: this.pagination.pageSize,
                "filter[replenishment_strategy]": this.replenishmentFilter.length ? this.replenishmentFilter.join(",") : null,
                "filter[purchase_status]": this.statusFilter.length ? this.statusFilter.join(",") : null,
                sort: this.sort.order === "descend" ? `-${this.sort.field}` : this.sort.field,
            };

            if (this.searchField && this.search) {
                params[`filter[${this.searchField}]`] = this.search;
            }

            return params;
        },
        load(page = 1) {
            this.loading = true;

            this.svc
                .products(this.$route.params.id, this.buildParams(page))
                .then((response) => response.data)
                .then((data) => {
                    this.rows = data.data || [];
                    this.pagination.total = data.meta.total;
                    this.pagination.current = data.meta.current_page;
                    this.pagination.pageSize = data.meta.per_page;
                    this.$emit("count-changed", this.pagination.total);
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        handleTableChange(pagination, filters, sorter) {
            this.pagination.pageSize = pagination.pageSize;
            this.sort = {
                field: sorter.order ? sorter.column.columnKey : "produto",
                order: sorter.order || "ascend",
            };
            this.load(pagination.current);
        },
        toQuickViewProduct(product) {
            return {
                id: product.pro_id,
                name: product.pro_descricao,
                brand: null,
                ean: product.pro_ean,
                internal_code: product.pro_codInterno,
            };
        },
        productActions(product) {
            return [
                {
                    label: "Análise de giro",
                    icon: "query_stats",
                    iconClass: "action-info",
                    onClick: () => this.$emit("giro", this.toQuickViewProduct(product), this.rows.map(this.toQuickViewProduct), true),
                },
                {
                    label: "Alterar tipo de reabastecimento",
                    icon: "sync_alt",
                    iconClass: "action-primary",
                    disabled: this.savingReplenishment === product.pro_id,
                    onClick: () => this.promptReplenishmentStrategy(product),
                },
            ];
        },
        promptReplenishmentStrategy(product) {
            this.$swal
                .fire({
                    title: "Alterar tipo de reabastecimento",
                    text: this.replenishment(product)
                        ? `${product.pro_descricao} — tipo atual: ${this.replenishment(product)}.`
                        : `${product.pro_descricao} — ainda sem tipo de reabastecimento definido.`,
                    input: "select",
                    inputOptions: REPLENISHMENT_LABELS,
                    inputValue: product.replenishment_strategy || undefined,
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "Salvar",
                    inputValidator: (value) => {
                        if (!value) {
                            return "Você precisa escolher um tipo!";
                        }
                    },
                })
                .then((result) => {
                    if (result.value && result.value !== product.replenishment_strategy) {
                        this.saveReplenishmentStrategy(product, result.value);
                    }
                });
        },
        saveReplenishmentStrategy(product, strategy) {
            this.savingReplenishment = product.pro_id;

            this.svc
                .update_replenishment_strategy(this.$route.params.id, product.pro_id, strategy)
                .then(() => {
                    product.replenishment_strategy = strategy;
                    this.$swal.fire("Atualizado!", "Reabastecimento alterado com sucesso.", "success");
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                    this.$swal.fire("Erro!", "Não foi possível atualizar o reabastecimento.", "error");
                })
                .finally(() => {
                    this.savingReplenishment = null;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.prd-tab {
    padding: 20px;
}
.prd-hd {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;

    h2 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.prd-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.prd-summary {
    display: flex;
    gap: 12px;
}
.summary-card {
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 6px;
    padding: 10px 16px;
    display: flex;
    align-items: center;
    gap: 8px;

    span {
        color: #8c8c8c;
        font-size: 13px;
    }

    b {
        font-size: 18px;
        color: #262626;
    }
}
.prd-filterbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
}
.prd-filter-select {
    width: 220px;
}
.fixed-height-select.ant-select,
.fixed-height-select ::v-deep(.ant-select-selection--multiple) {
    min-height: 32px !important;
    max-height: 32px !important;
    overflow: auto;
}
.prd-search-group {
    display: flex !important;
    flex: 1 1 240px;
    min-width: 220px;
    max-width: 340px;
}
.prd-search-group-field {
    width: 130px;
    flex: 0 0 auto;
}
.prd-search-group-input {
    flex: 1;
    min-width: 0;
}
.prd-search-group ::v-deep(.ant-select-selector),
.prd-search-group ::v-deep(.ant-input) {
    height: 32px !important;
}
.prd-card {
    --pri: var(--primary-color);
    width: 100%;

    ::v-deep(.ant-card-body) {
        width: 100%;
        padding: 0;
    }
}
.pro-name {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    color: #262626;
}
.pro-ean {
    font-size: 11px;
    color: #8c8c8c;
}
.req-empty {
    color: #bfbfbf;
    font-style: italic;
}
.status-tag {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 10px;
    background: #fff7e6;
    color: #d46b08;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}
.status-tag-purchased {
    background: #f6ffed;
    color: #389e0d;
}
.repl-tag {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 10px;
    background: #f0f0f0;
    color: #595959;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}
</style>
